import { useGetAlphaBscPositionsQuery } from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const BW3W_URL = "https://www.binance.com/en/web3wallet";

/**
 * Web3WalletExitCard — primary path for exiting alpha-bsc positions
 * whose on-chain Uniswap/PancakeSwap swap is blocked by transfer-tax
 * / anti-bot SafeTransferFrom errors. The operator imports the bot's
 * BSC trading wallet into Binance Web3 Wallet and sells the tokens
 * directly using Binance's aggregated swap routes — no software fix
 * possible on our side for these token contracts.
 *
 * Shows currently-open positions as a quick reference so the operator
 * can spot which ones need attention.
 */
export function Web3WalletExitCard() {
  const positions = useGetAlphaBscPositionsQuery("open", { pollingInterval: 60000 });
  const rows: any[] = (positions.data as any) || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
          <span>Stuck position exit · Binance Web3 Wallet</span>
          <a
            href={BW3W_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 h-9 px-4 text-sm rounded-md font-medium bg-accent text-white hover:bg-accent/90 transition-colors"
          >
            Open Binance Web3 Wallet ↗
          </a>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted leading-relaxed">
          Some Alpha-BSC tokens (NEX, MYX, BILL, etc.) have transfer-tax /
          anti-bot contracts that fail with <code>SafeTransferFrom</code> on
          Uniswap / PancakeSwap. The bot's BSC trading wallet is imported
          into Binance Web3 Wallet — sell those positions there directly
          using Binance's aggregated swap routes. Once a sell lands on-chain,
          the next graduation-watcher tick will reconcile the position.
        </p>

        {rows.length === 0 ? (
          <div className="text-xs text-muted">No open alpha-bsc positions.</div>
        ) : (
          <div>
            <div className="text-xs text-muted mb-1">Currently open:</div>
            <div className="border border-border rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-bg2 text-left text-muted">
                  <tr>
                    <th className="py-1 px-2">Symbol</th>
                    <th className="py-1 px-2">Contract</th>
                    <th className="py-1 px-2 text-right">Entry $</th>
                    <th className="py-1 px-2 text-right">Now $</th>
                    <th className="py-1 px-2 text-right">P&amp;L</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p: any) => {
                    const pnl = Number(p.unrealized_pnl_pct ?? 0);
                    const short = (p.contract_address || "").slice(0, 8);
                    return (
                      <tr key={p.id} className="border-t border-border/40">
                        <td className="py-1 px-2 font-mono font-semibold">{p.symbol}</td>
                        <td className="py-1 px-2 font-mono text-muted">
                          <a
                            href={`https://bscscan.com/token/${p.contract_address}`}
                            target="_blank" rel="noopener noreferrer"
                            className="hover:underline"
                          >{short}…</a>
                        </td>
                        <td className="py-1 px-2 text-right font-mono">
                          ${Number(p.entry_size_usdt || 0).toFixed(2)}
                        </td>
                        <td className="py-1 px-2 text-right font-mono">
                          {p.current_value_usdt != null
                            ? `$${Number(p.current_value_usdt).toFixed(2)}`
                            : "—"}
                        </td>
                        <td className={`py-1 px-2 text-right font-mono ${pnl > 0 ? "text-emerald-400" : pnl < 0 ? "text-rose-400" : ""}`}>
                          {p.unrealized_pnl_pct != null
                            ? `${pnl > 0 ? "+" : ""}${pnl.toFixed(2)}%`
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="text-xs text-muted">
          <Badge tone="muted">tip</Badge>{" "}
          The on-chain bot stays in charge of normal exits — open
          BW3W only when the bot logs an <code>STF</code> /{" "}
          <code>SafeTransferFrom</code> failure for a sell route.
        </div>
      </CardContent>
    </Card>
  );
}
